// Builds the station's data layer for the shell the app runs in (D-019):
//   Electron (the preload exposes `stationHost.sync`) → IpcSyncEngine → @powersync/node in main
//   Capacitor native                                   → PowerSync Capacitor (beta)
//   anything else (a browser, Playwright)              → PowerSync web (IndexedDB)
// Secrets: Electron safeStorage, with the refresh secret sealed by the main process (the page
// never holds it in clear, CR8B-1); Capacitor Keychain/Keystore; a browser IndexedDB, which is
// DEVELOPMENT ONLY (StationData refuses it on a native platform).

import {
  ApiClient,
  deviceAuthFetch,
  ElectronSecureStore,
  IndexedDbSecureStore,
  IpcSyncEngine,
  type SecureStore,
  StationData,
  type SyncEngine,
  type SyncIpcChannel,
} from '@enp-suite/data';
import type { DevicePlatform } from '@enp/domain';
import appPackage from '../../package.json';
import { apiBaseUrlFor, syncUrlFor } from './service-urls.js';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

const capacitor = () => (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
const isCapacitorNative = () => capacitor()?.isNativePlatform?.() === true;

/** The Electron preload's channel to the main-process engine. */
const electronSync = (): SyncIpcChannel | undefined =>
  (window.stationHost as { sync?: SyncIpcChannel } | undefined)?.sync;

function platform(): DevicePlatform {
  if (window.stationHost?.platform === 'electron') return 'windows';
  if (isCapacitorNative()) return capacitor()?.getPlatform?.() === 'ios' ? 'ios' : 'android';
  return 'web';
}

/** The API the station talks to (see service-urls.ts). */
export function apiBaseUrl(): string {
  return apiBaseUrlFor(import.meta.env.VITE_ENP_API_URL, window.location);
}

async function selectEngine(): Promise<SyncEngine> {
  const ipc = electronSync();
  if (ipc) return new IpcSyncEngine(ipc);
  if (isCapacitorNative()) {
    const { createCapacitorSyncEngine } = await import('@enp-suite/data/capacitor');
    return createCapacitorSyncEngine();
  }
  const { createWebSyncEngine } = await import('@enp-suite/data/web');
  return createWebSyncEngine();
}

async function selectSecureStore(): Promise<SecureStore> {
  const host = window.stationHost;
  if (host?.platform === 'electron') return new ElectronSecureStore(host.secureStorage);
  if (isCapacitorNative()) {
    const { createCapacitorSecureStore } = await import('@enp-suite/data/capacitor');
    return createCapacitorSecureStore();
  }
  return new IndexedDbSecureStore();
}

/**
 * The API client. On Windows, pairing and token requests go through the shell's main
 * process, so the page never holds the refresh secret in clear (review CR8B-1).
 */
function apiClient(): ApiClient {
  const baseUrl = apiBaseUrl();
  const deviceAuth = window.stationHost?.deviceAuth;
  return new ApiClient(
    deviceAuth ? { baseUrl, fetch: deviceAuthFetch(deviceAuth, baseUrl) } : { baseUrl },
  );
}

export async function createStationData(): Promise<StationData> {
  return new StationData({
    engine: await selectEngine(),
    api: apiClient(),
    store: await selectSecureStore(),
    platform: platform(),
    appVersion: appPackage.version,
    syncEndpoint: syncUrlFor(import.meta.env.VITE_ENP_SYNC_URL, window.location),
  });
}
